<script>
import {
  getAutoClockOutFilters, getWarrantyWorkTimeTrackings, markWarrantyWorkAsBilled, getWarrantyWorkDocuments
} from '@/api/service'
import DateHelpers from '@/mixins/DateHelpers'
import { DateTime } from 'luxon'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import {
  PresetFilter, DateFromFilter, DateToFilter, MechanicFilter
} from '@/components/service/filters'
import { mapGetters } from 'vuex'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import WarrantyOrderDocumentModal from '@/components/service/WarrantyOrderDocumentModal.vue'
import EditWarrantyOrderDocumentModal from '@/components/service/EditWarrantyOrderDocumentModal.vue'

export default {
  components: {
    'mia-edit-warranty-order-document-modal': EditWarrantyOrderDocumentModal,
    'mia-date-from-filter': DateFromFilter,
    'mia-date-to-filter': DateToFilter,
    'mia-mechanic-filter': MechanicFilter,
    'mia-preset-filter': PresetFilter,
    'mia-warranty-order-document-modal': WarrantyOrderDocumentModal
  },

  mixins: [DateHelpers, FormattingHelpers],

  data() {
    return {
      employees: [],
      timeTrackings: [],
      documents: [],
      billableOrderId: null,
      showCustomWarrantyDocumentModal: false,
      selectedWarrantyDocument: null,
      timeTrackingColumns: [
        {
          field: 'item_date',
          label: 'Datum',
          headerAlign: 'left',
          representedAs: row => DateTime.fromSQL(row.item_date).toFormat('dd.MM.yyyy')
        },
        {
          field: 'item_order_id',
          label: 'Auftragsnummer',
          headerAlign: 'left'
        },
        {
          field: 'item_fin_ends_with',
          label: 'FIN (letzte 6 Zahlen)',
          headerAlign: 'left'
        },
        {
          field: 'item_user_id',
          label: 'Mechaniker',
          headerAlign: 'left',
          representedAs: row => (row.item_user_id > 0 ? `${row.item_user_firstname} ${row.item_user_lastname}` : '')
        },
        {
          field: 'item_hours_sold',
          label: 'AW verkauft',
          headerAlign: 'left'
        },
        {
          field: 'item_comment',
          label: 'Kommentar',
          headerAlign: 'left'
        },
        {
          label: '',
          field: '',
          headerAlign: 'left',
          align: 'right',
          component: ActionButtonColumn,
          meta: {
            actions: [
              {
                icon: 'check-payment-sign',
                class: 'border-green-600 text-green-600 hover:bg-green-600',
                tooltip: 'Beiblatt erstellen',
                attrs: {
                  title: 'Beiblatt erstellen'
                },
                onClick: (row) => {
                  this.billableOrderId = row.item_order_id
                }
              }
            ]
          }
        }
      ],
      documentColumns: [
        {
          field: 'item_created_at',
          label: 'Datum',
          headerAlign: 'left',
          representedAs: row => DateTime.fromSQL(row.item_created_at).toFormat('dd.MM.yyyy')
        },
        {
          field: 'item_order_id',
          label: 'Auftragsnummer',
          headerAlign: 'left'
        },
        {
          field: 'item_make',
          label: 'Hersteller',
          headerAlign: 'left',
          representedAs: row => ({
            SEAT_CUPRA: 'Seat/Cupra',
            SKODA: 'Skoda'
          }[row.item_make] || '')
        },
        {
          label: '',
          field: '',
          headerAlign: 'left',
          align: 'right',
          component: ActionButtonColumn,
          meta: {
            actions: [
              {
                icon: 'check-payment-sign',
                class: 'border-green-600 text-green-600 hover:bg-green-600',
                tooltip: 'Beiblatt anzeigen',
                attrs: {
                  title: 'Beiblatt anzeigen'
                },
                onClick: (row) => {
                  this.selectedWarrantyDocument = row
                }
              }
            ]
          }
        }
      ]
    }
  },

  computed: {
    ...mapGetters('service', ['globalFilters'])
  },

  created() {
    this.loadFilters()
    this.loadWarrantyWorkTimeTrackings()
    this.loadWarrantyWorkDocuments()

    if (!this.isAllowed('moderate_service_mechanics')) {
      this.$store.commit('service/setGlobalMechanic', this.authUser.item_zls_id)
    }
  },

  methods: {
    async loadFilters() {
      this.$wait.start('loading filters')
      const [error, response] = await getAutoClockOutFilters()
      this.$wait.end('loading filters')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Filter konnten nicht geladen werden']
        })
        return
      }

      this.employees = response.employees
    },

    async loadWarrantyWorkTimeTrackings() {
      this.$wait.start('loading warranty work time trackings')
      const [error, response] = await getWarrantyWorkTimeTrackings({
        date_from: this.globalFilters.dateFrom || undefined,
        date_to: this.globalFilters.dateTo || undefined,
        employee: this.globalFilters.mechanic || undefined
      })
      this.$wait.end('loading warranty work time trackings')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Die Zeiterfassung konnten nicht geladen werden']
        })
        return
      }

      this.timeTrackings = response
    },

    async loadWarrantyWorkDocuments() {
      this.$wait.start('loading warranty work documents')
      const [error, response] = await getWarrantyWorkDocuments({
        date_from: this.globalFilters.dateFrom || undefined,
        date_to: this.globalFilters.dateTo || undefined,
        employee: this.globalFilters.mechanic || undefined
      })
      this.$wait.end('loading warranty work documents')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Die Dokumente konnten nicht geladen werden']
        })
        return
      }

      this.documents = response
    },

    async onDocumentSaved(timeTrackingIds) {
      this.loadWarrantyWorkDocuments()

      if (timeTrackingIds.length === 0) {
        this.loadWarrantyWorkTimeTrackings()
        return
      }

      const [error] = await markWarrantyWorkAsBilled({ ids: timeTrackingIds })

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['ZE-Einträge konnten nicht aktualisiert werden']
        })
        return
      }

      this.loadWarrantyWorkTimeTrackings()
    }
  }
}
</script>

<template>
  <div>
    <div class="grid grid-cols-5 gap-6 p-4 bg-gray-200">
      <mia-preset-filter @preset-changed="loadWarrantyWorkTimeTrackings" />
      <mia-date-from-filter @blur-with-change="loadWarrantyWorkTimeTrackings" />
      <mia-date-to-filter @blur-with-change="loadWarrantyWorkTimeTrackings" />
      <mia-mechanic-filter
        v-if="isAllowed('moderate_service_mechanics')"
        :mechanics="employees"
        @change="loadWarrantyWorkTimeTrackings"
      />
      <div class="flex justify-end items-end">
        <button
          type="button"
          class="inline-flex items-center border-2 border-green-600 rounded py-2 px-3 text-green-800 hover:text-white hover:bg-green-600 focus:outline-none"
          @click="showCustomWarrantyDocumentModal = true"
        >
          <mia-streamline-icon
            class="w-5 h-5 fill-current"
            src="check-payment-sign"
          />
          <span class="ml-2">Beiblatt erstellen</span>
        </button>
      </div>
    </div>

    <div class="mt-6">
      <mia-tabs>
        <mia-tab
          id="timeTrackings"
          label="Zeiterfassung"
        >
          <mia-wait
            for="loading warranty work time trackings"
            class="mt-2"
          >
            <template #waiting>
              <div class="flex items-center justify-center py-8">
                <mia-wave-loader />
              </div>
            </template>

            <template #default>
              <datatable
                ref="datatableRef"
                :columns="timeTrackingColumns"
                :data="timeTrackings"
                class="sticky w-full table-auto datatable"
              >
                <template #no-results>
                  <div class="flex justify-center">
                    Keine Einträge gefunden.
                  </div>
                </template>
              </datatable>
            </template>
          </mia-wait>
        </mia-tab>
        <mia-tab
          id="documents"
          label="Gespeicherte Dokumente"
        >
          <mia-wait
            for="loading warranty work documents"
            class="mt-2"
          >
            <template #waiting>
              <div class="flex items-center justify-center py-8">
                <mia-wave-loader />
              </div>
            </template>

            <template #default>
              <datatable
                ref="datatableRef"
                :columns="documentColumns"
                :data="documents"
                class="sticky w-full table-auto datatable"
              >
                <template #no-results>
                  <div class="flex justify-center">
                    Keine Einträge gefunden.
                  </div>
                </template>
              </datatable>
            </template>
          </mia-wait>
        </mia-tab>
      </mia-tabs>
    </div>

    <mia-warranty-order-document-modal
      v-if="billableOrderId"
      :open="true"
      :time-trackings="timeTrackings"
      :order-id="billableOrderId"
      @closed="billableOrderId = null"
      @cancel="billableOrderId = null"
      @saved="onDocumentSaved"
    />
    <mia-warranty-order-document-modal
      v-else-if="showCustomWarrantyDocumentModal"
      :open="true"
      @closed="showCustomWarrantyDocumentModal = false"
      @cancel="showCustomWarrantyDocumentModal = false"
      @saved="onDocumentSaved"
    />
    <mia-edit-warranty-order-document-modal
      v-else-if="selectedWarrantyDocument"
      :saved-document="selectedWarrantyDocument"
      :time-trackings="timeTrackings"
      :open="true"
      @saved="onDocumentSaved"
      @closed="selectedWarrantyDocument = null"
      @cancel="selectedWarrantyDocument = null"
    />
  </div>
</template>
